import { createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { mainnet, dymension } from '@reown/appkit/networks';
import { defineChain } from '@reown/appkit/networks';
import { getMainCurrency } from '../../currency/currency-service';
import { Network } from '../../network/network-types';
import { EthereumWallet, EthereumProvider } from './ethereum-wallet';
import { WalletType } from '../wallet-types';

const appKitInstance = createAppKit({
    adapters: [ new EthersAdapter() ],
    networks: [ mainnet, dymension ],
    defaultNetwork: mainnet,
    projectId: 'bdf618859e3d4cd5672a1de10041fd57',
    enableWalletGuide: false,
    allWallets: 'HIDE',
    features: { email: false, socials: false, allWallets: false },
});

export class WalletConnectWallet extends EthereumWallet {

    constructor() {
        super('');
    }

    public clear(): void {
        appKitInstance.disconnect().then();
    }

    async switchNetwork(network: Network): Promise<void> {
        if (!network.evm?.rpc) {
            return;
        }
        const nativeCurrency = getMainCurrency(network);
        const chainId = Number(network.evm.chainId);
        const customNetwork = defineChain({
            id: chainId,
            chainNamespace: 'eip155',
            caipNetworkId: `eip155:${chainId}`,
            name: network.chainName,
            nativeCurrency: {
                name: nativeCurrency.displayDenom,
                symbol: nativeCurrency.displayDenom,
                decimals: nativeCurrency.decimals,
            },
            rpcUrls: {
                default: {
                    http: [ network.evm.rpc ],
                    webSocket: [ network.evm.rpc.replace(/^(http)/, 'ws') ],
                },
            },
            testnet: process.env.REACT_APP_ENV !== 'mainnet',
        });
        appKitInstance.addNetwork('eip155', customNetwork);
        return super.switchNetwork(network);
    }

    public async connect(): Promise<void> {
        if (appKitInstance.getProvider('eip155')) {
            return Promise.resolve();
        }
        await appKitInstance.open({ view: 'ConnectingWalletConnectBasic', namespace: 'eip155' });
        return new Promise<void>((resolve, reject) => {
            const unsubscribe = appKitInstance.subscribeEvents((event) => {
                if (event.data.event === 'MODAL_CLOSE') {
                    unsubscribe();
                    if (event.data.properties.connected) { resolve(); } else { reject(); }
                }
            });
        });
    }

    public getWalletType(): WalletType {
        return 'WalletConnect';
    }

    public validateWalletInstalled(): Promise<void> {
        return Promise.resolve();
    }

    public async getAccounts(network?: Network): Promise<string[]> {
        const provider = appKitInstance.getProvider('eip155');
        if (!provider) {
            return [];
        }
        return super.getAccounts();
    }

    public async getProvider(): Promise<EthereumProvider> {
        const provider = appKitInstance.getProvider('eip155');
        if (provider) {
            return provider as EthereumProvider;
        }
        return new Promise<EthereumProvider>((resolve) => {
            const unsubscribe = appKitInstance.subscribeProviders((providers) => {
                if (providers.eip155) {
                    unsubscribe();
                    resolve(providers.eip155 as EthereumProvider);
                }
            });
        });
    }

    public getCurrentProvider(): EthereumProvider | undefined {
        return undefined;
    }
}
